import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, usePlaybooksApi } from '../apis/playbooks-apis';
import { useTagsApi } from '../apis/tags-apis';
import { useProductsApi } from '../apis/products-apis';
import { Playbook } from '../types';
import { useToast } from "../components/ui/Toast";
import { useNavigation } from '../contexts/NavigationContext';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import PlaybookForm, { PlaybookFormRef } from './PlaybookForm';
import { Spinner } from "../components/ui/Spinner";
import { Loader2 } from 'lucide-react';

const LoadingOverlay = ({ msg }: { msg?: string }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
        <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
        <span className="text-lg font-medium">{msg || 'Please wait'}</span>
      </div>
    </div>
  )
}

export const EditPlaybookPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const initialFetchMade = useRef(false);
  const formRef = useRef<PlaybookFormRef>(null);
  
  const [playbookAgenticgenie, setPlaybook] = useState<Playbook | null>(null);
  const [originalPlaybook, setOriginalPlaybook] = useState<Playbook | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [existingPlaybooks, setExistingPlaybooks] = useState<Playbook[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState('');
  const { 
    showNavigationWarning, 
    pendingNavigation, 
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();
  const playbooksApi = usePlaybooksApi();
  const productsApi = useProductsApi();
  const tagsApi = useTagsApi();

  const hasUnsavedChanges = !isReadOnly && 
    JSON.stringify(playbookAgenticgenie) !== JSON.stringify(originalPlaybook);

  // Handle browser back button
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  };

  // Set up block for browser back button
  useEffect(() => {
    // Add extra history entry when editing starts
    if (hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
    }

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges) {
        // Push state back to prevent navigation
        window.history.pushState(null, '', window.location.pathname);
        // Show warning dialog
        setShowNavigationWarning(true);
        setPendingNavigation('/studio/playbooks');
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, handleBeforeUnload, setShowNavigationWarning, setPendingNavigation]);



  useEffect(() => {
    const fetchData = async () => {
      // Early return if data fetch is not needed
      if (!id || initialFetchMade.current) {
        return;
      }
      
      initialFetchMade.current = true;
      
      try {
        // Fetch playbook and related data
        const getAllPlaybooks = true;
        const [playbookData, allPlaybooks] = await Promise.all([
          playbooksApi.getPlaybook(id),
          playbooksApi.getPlaybooks(getAllPlaybooks)
        ]);
        
        setPlaybook(playbookData);
        setOriginalPlaybook(playbookData);
        setExistingPlaybooks(allPlaybooks);
        
        // Load tag suggestions
        try {
          const globalTags = await tagsApi.getTags();
          setTagSuggestions(globalTags.map(tag => tag.tagName));
        } catch (tagError) {
          console.error('Error fetching global tags:', tagError);
          // Fallback to extracting tags from existing playbooks if global tags fail
          const allTags = new Set<string>();
          allPlaybooks.forEach(playbook => {
            playbook.playbookTags?.forEach(tag => {
              if (tag) allTags.add(tag);
            });
          });
          setTagSuggestions(Array.from(allTags));
        }
      } catch (error: any) {
        console.error('Error fetching data:', error);
        
        if (error instanceof ApiError && error.status === 403) {
          navigate('/studio/unauthorized');
          return;
        }
        
        // For all other errors, show the specific error message or fallback
        const errorMessage = error instanceof ApiError 
          ? error.message 
          : "Failed to fetch playbook details";
          
        addToast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
          duration: 5000
        });
        navigate('/studio/playbooks');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, addToast]);

  const handleSubmit = async () => {
    if (!playbookAgenticgenie) return;

    const isValid = await formRef.current?.validateForm() ?? false;
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    try {
      setIsSaving(true);
      setOverlayMessage('Saving Playbook... Please wait');
      // Check if product name has changed
      const productNameChanged = originalPlaybook?.playbookProductName !== playbookAgenticgenie.playbookProductName;
      
      // Handle product creation/update if needed
      if (productNameChanged && playbookAgenticgenie.playbookProductName) {
        try {
          // This will either find the existing product or create a new one
          await productsApi.findOrCreateProduct(playbookAgenticgenie.playbookProductName);
        } catch (productError) {
          console.error('Error handling product:', productError);
          addToast({
            title: "Warning",
            description: "There was an issue with the product, but the playbook update will proceed",
            variant: "default",
            duration: 4000
          });
          // Continue with playbook update despite product issues
        }
      }
      
      // Update the playbook
      await playbooksApi.updatePlaybook(playbookAgenticgenie);
      const savedPlaybook = { ...playbookAgenticgenie };
      setOriginalPlaybook(savedPlaybook);
      setPlaybook(savedPlaybook);
      
      addToast({
        title: "Success",
        description: "Playbook " + playbookAgenticgenie.playbookName + " has been successfully updated",
        variant: "default",
        duration: 4000
      });
        
      
      setOverlayMessage('Building Playbook... Please wait');

      // Build playbook - this is optional and shouldn't block the user experience
      try {
        const response = await playbooksApi.buildPlaybook(playbookAgenticgenie.playbookId);
        if (response?.["status"] == "success") {
          console.log("Successfully initialized playbook", response);
          // Note: We don't show success toast for initialization as it's an internal process
        } else {
          // Log warning but don't show error to user - initialization is optional
          console.warn("Playbook initialization failed but playbook update was successful:", response?.["message"]);
        }
      } catch (error) {
        // Log warning but don't show error to user - initialization is optional
        console.warn("Playbook initialization failed but playbook update was successful:", error);
      }
      
      setOverlayMessage('');

      // Navigate back to playbooks list after successful save
      navigate('/studio/playbooks');

      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Error updating playbook:', error);
      addToast({
        title: "Error",
        description: "Failed to update playbook: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/studio/playbooks');
    } else {
      navigate('/studio/playbooks');
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    } else {
      navigate('/studio/playbooks');
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/studio/playbooks');
    } else {
      navigate('/studio/playbooks');
    }
  };

  if (loading || !playbookAgenticgenie) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-sky-500" />
        <p className="text-center text-muted-foreground">Loading Playbooks...</p>
      </div>
    );
  }

  return (
    <>
      {isSaving && <LoadingOverlay msg={overlayMessage} />}
      
      <PlaybookForm 
        ref={formRef}
        playbook={playbookAgenticgenie}
        originalPlaybook={originalPlaybook}
        onPlaybookChange={setPlaybook}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={true}
        readOnly={false}
        onDiscardChanges={handleDiscardChanges}
        hasUnsavedChanges={hasUnsavedChanges}
        setIsReadOnly={setIsReadOnly}
        existingPlaybooks={existingPlaybooks}
        tagSuggestions={tagSuggestions}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default EditPlaybookPage;